// O Campus Agzos desenhado no canvas: cidade, prédios, andares, salas e os personagens
// andando de verdade (A*) para onde o status manda.
//
// Mesma API pública do renderer antigo (applySnapshot, applyEvent, select, start, resize),
// para a UI não precisar saber qual está em uso.

import { CAST_BY_ID, castFor } from '../core/cast.js';
import { stableHash } from '../core/hash.js';
import type { Look } from '../core/look.js';
import type { Activity, Agent, OfficeEvent, OfficeSnapshot, PermissionRequest, PublicSettings, Room, SocialProfile } from '../core/types.js';
import { TILE, type Dir } from '../core/world.js';
import { FURNITURE_BY_ID } from './art/furniture/index.js';
import { resolveToken } from './art/palette.js';
import { TILE_BY_ID } from './art/tiles.js';
import { CHAR_H, CHAR_W, SIT_DY, type FurnitureDef, type PixelSprite } from './art/types.js';
import { bake, ctx2d, makeCanvas } from './engine/bake.js';
import { Camera } from './engine/camera.js';
import { characterFrame, type Pose } from './engine/character.js';
import { Gestos, INERCIA_MS, inercia, TOQUE_TOLERANCIA, type Gesto, type Ponto } from './gestures.js';
import { buildRoom, type BuiltRoom } from './engine/room.js';
import { paintScreen, renderScene, type Drawable, type Overlay } from './engine/scene.js';
import { chooseSpot, dayPhase, entryOf, floorSpots, idleChats, isRoomDark, leaveAlpha, poseAt, screenFrame, screenMode, type Seated, type WorldSpot } from './world/behavior.js';
import { alvoDaSaida, avisaElevador, SAIDA_FADE_MS } from './world/saida.js';
import { aproximaEscuro } from './world/luz.js';
import { AMPULHETA_ALTURA, AMPULHETA_QUADROS, ampulhetaQuadro } from './art/ampulheta.js';
import { layoutCampus, type CampusBuilding, type CampusFloor, type CampusLayout, type PlacedMap } from './world/layout.js';
import { findPath, floorGrid, type FloorGrid } from './world/path.js';
import { arrangeLabels, conteudoDoCartao, textoDoBalao, type LabelBox } from './world/labels.js';
import { t } from './i18n.js';
import { Rodas, nomeDoElenco } from './world/rodas.js';
import { tocaRoda } from './sound.js';
import { GithubFx, type FxHost } from './github-fx.js';
import { cadenciaDoDesenho, dprDoCampo, telasEfetivas, type Bateria } from './desempenho.js';
import { ehCelular, observaBateria } from './mobile.js';
import { PROVIDER_COLORS, PROVIDER_TAGS } from './sprites.js';
import { seasonalDecor, type Season } from './art/seasonal.js';
import { WALL_H } from '../core/world.js';
import { FAROL, FAROL_COR } from './art/farol.js';
import { VAGALUME, VAGALUME_COR } from './art/vaga-lume.js';
import { chapeuDaEpoca, corDoCeu, faroisDoCarro, horaAgora, posicaoDoVagalume, raioDoSol, temaEfetivo, vagalumesNoJardim, type Vagalume } from './world/ceu.js';
import { brilho, camadaDoSol, desenhaChapeu } from './world/ceu-desenho.js';

/** Velocidade de caminhada, em px de arte por ms. */
const SPEED = 0.05;
/** Quanto tempo o balão de atividade fica na tela; o 'concluiu em …' fica mais, para dar tempo de ler. */
const BUBBLE_MS = 7000;
const DONE_BUBBLE_MS = 12000;
/** Nos últimos ms, o balão some aos poucos. */
const FADE_MS = 800;
/** Sala apagada: escurece o piso, a mobília e as luzes dela. */
const DARK_ROOM = 'rgba(6, 8, 16, 0.6)';

export interface CampusRendererOptions {
  onSelect?: (agentId: string | null) => void;
  /** A câmera passou a seguir outro agente, ou parou de seguir (null). */
  onFollow?: (agentId: string | null) => void;
}

interface Actor {
  id: string;
  /** Pé do personagem, em px de arte. */
  x: number;
  y: number;
  tx: number;
  ty: number;
  path: Array<{ x: number; y: number }>;
  dir: Dir;
  spot: WorldSpot | undefined;
  floorKey: string;
  /** Último agente visto: quem já saiu da lista ainda é desenhado enquanto anda para fora. */
  agent: Agent;
  /**
   * Saindo (a sessão fechou): anda até o elevador do andar (ou à porta da sala) e some lá. `until` é
   * quando some (performance.now), null enquanto ainda anda; `elevador` diz se a saída é pelo elevador.
   */
  leaving: { until: number | null; elevador: boolean } | null;
}

interface BuiltFloor {
  key: string;
  building: CampusBuilding;
  floor: CampusFloor;
  backdrop: { canvas: HTMLCanvasElement; x: number; y: number };
  rooms: BuiltRoom[];
  grid: FloorGrid;
  spots: WorldSpot[];
}

interface Prop {
  def: FurnitureDef;
  x: number;
  y: number;
  flip: boolean;
  /** Ordem de desenho, quando não for a base (itens de parede). */
  sortY?: number;
}

interface Car {
  def: FurnitureDef;
  x: number;
  y: number;
  speed: number;
}

const color = (token: string) => resolveToken(token) ?? '#ff00ff';
const floorKey = (building: string, level: number) => `${building}/${level}`;

export class CampusRenderer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly camera = new Camera();
  private readonly agentMap = new Map<string, Agent>();
  private readonly actors = new Map<string, Actor>();
  private readonly reserved = new Map<string, string>();
  /** As rodas dos ociosos (TV, jogos, papo…): quem senta onde, placar e balões (world/rodas.ts). */
  private readonly rodas = new Rodas();
  private readonly activeFloor = new Map<string, number>();
  /** Desde quando (performance.now) cada sala de projeto está vazia; quem tem gente não aparece aqui. */
  private roomEmpty = new Map<string, number>();
  /** GitHub no escritório: festa e alarme na sala (github-fx.ts). */
  private readonly gh = new GithubFx();
  /** Quanto escuro está cada sala de projeto (0 = acesa, 1 = apagada), passando suave (luz.ts). */
  private escuro = new Map<string, number>();
  /** Balões de conversa dos ociosos neste quadro: id do agente → frase. */
  private chats = new Map<string, string>();
  /** Vida social por id do elenco (vem do servidor): o balão mostra a atividade atual. */
  private social: Record<string, SocialProfile> = {};
  /** Timelapse (B21): status de cada agente no quadro mostrado; null = ao vivo. */
  private playing: Map<string, Agent['status']> | null = null;
  private rooms: Room[] = [];
  private permissions: PermissionRequest[] = [];
  /** Ajustes públicos do CMS (layout, tema). */
  private settings: PublicSettings | undefined;
  private layout: CampusLayout | null = null;
  private signature = '';
  private floors = new Map<string, BuiltFloor>();
  private exterior: Array<{ canvas: HTMLCanvasElement; x: number; y: number }> = [];
  private props: Prop[] = [];
  private cars: Car[] = [];
  /** Época do calendário ou do ajuste (ceu.ts temaEfetivo) e o enfeite da cabeça dos personagens nela. */
  private tema: Season = 'padrao';
  private chapeu: PixelSprite | null = null;
  /** Vaga-lumes do jardim (posições de repouso, em px de arte). */
  private vagalumes: Vagalume[] = [];
  private selected: string | null = null;
  private follow: string | null = null;
  private tabs: Array<{ x: number; y: number; w: number; h: number; building: string; level: number }> = [];
  private drag: { x: number; y: number; moved: boolean } | null = null;
  /** Toques com um ou dois dedos (gestures.ts); o mouse continua pelo `drag` e pelo clique de sempre. */
  private readonly gestos = new Gestos();
  /** Arrasto solto com velocidade (px CSS/ms): a câmera desacelera por INERCIA_MS. `feito` é o quanto já andou. */
  private inerciaAtiva: { vx: number; vy: number; t0: number; feito: { dx: number; dy: number } } | null = null;
  /** Cartão do toque longo: o personagem e onde o dedo estava (px CSS da janela). Some ao tocar fora. */
  private toque: { id: string; x: number; y: number } | null = null;
  /** Última atividade de cada agente e quando ela chegou (performance.now): vira balão e linha do cartão. */
  private lastActivity = new Map<string, { activity: Activity; at: number }>();
  /** Mouse sobre o canvas (x/y em px do canvas; cx/cy na janela), ou null: fora do canvas e no toque. */
  private hover: { x: number; y: number; cx: number; cy: number } | null = null;
  /** Cartão ao passar o mouse: um div fixo no corpo da página, criado na primeira vez. */
  private card: HTMLDivElement | null = null;
  private cardKey = '';
  private fitted = false;
  private loadedAt = 0;
  private running = false;
  private raf = 0;
  private last = 0;
  /** Versão de celular (B29-M6): 30 fps, teto do dpr, pausa com a aba oculta e telas com bateria baixa. */
  private celular = false;
  private abaOculta = false;
  private bateria: Bateria | null = null;
  private bateriaLida = false;
  /** Instante do próximo desenho no celular (cadenciaDoDesenho, desempenho.ts). */
  private proximoDesenho = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly opts: CampusRendererOptions = {},
  ) {
    this.ctx = ctx2d(canvas);
    this.resize();
    this.bindInput();
    document.addEventListener('visibilitychange', () => this.mudouAba());
    // o campo muda de tamanho quando abre o painel do agente, não só quando a janela muda: sem isso
    // o desenho fica esticado e o clique e o hover caem no lugar errado
    const palco = canvas.parentElement;
    if (palco) new ResizeObserver(() => this.resize()).observe(palco);
  }

  // ------------------------------------------------------------------ API

  resize(): void {
    const parent = this.canvas.parentElement;
    this.celular = ehCelular();
    // a bateria só entra nas telas no celular: é lida na primeira vez que o campus fica de celular
    if (this.celular && !this.bateriaLida) {
      this.bateriaLida = true;
      void observaBateria((b) => {
        this.bateria = b;
      });
    }
    const dpr = this.dpr();
    this.canvas.width = Math.max(1, Math.round((parent?.clientWidth ?? 800) * dpr));
    this.canvas.height = Math.max(1, Math.round((parent?.clientHeight ?? 450) * dpr));
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
  }

  applySnapshot(snapshot: OfficeSnapshot): void {
    this.agentMap.clear();
    for (const agent of snapshot.agents) this.agentMap.set(agent.id, agent);
    this.rooms = snapshot.rooms;
    this.permissions = snapshot.permissions;
    if (snapshot.settings) this.settings = snapshot.settings;
    this.social = snapshot.social ?? {};
    for (const activity of snapshot.activities.slice(-30)) this.lembraAtividade(activity, false);
    this.loadedAt = performance.now();
    this.sync();
  }

  applyEvent(event: OfficeEvent): void {
    switch (event.type) {
      case 'snapshot':
        this.applySnapshot(event.snapshot);
        break;
      case 'agents':
        for (const id of event.removed ?? []) this.agentMap.delete(id);
        for (const agent of event.agents) this.agentMap.set(agent.id, agent);
        this.rooms = event.rooms;
        this.sync();
        break;
      case 'activity':
        this.lembraAtividade(event.activity, true);
        break;
      case 'social':
        this.social = { ...this.social, ...event.social };
        break;
      case 'github': {
        const room = this.rooms.find((r) => r.agentIds.includes(event.agentId));
        this.gh.onEvent(event, performance.now(), this.githubHost(), room?.agentIds ?? []);
        break;
      }
      case 'permission':
        this.permissions = [...this.permissions, event.request];
        break;
      case 'settings':
        this.settings = event.settings;
        this.signature = '';
        this.sync();
        break;
      case 'permission-resolved':
        this.permissions = this.permissions.filter((p) => p.id !== event.id);
        break;
      default:
        break;
    }
  }

  select(agentId: string | null): void {
    this.selected = agentId;
    this.setFollow(agentId);
    const home = agentId ? this.layout?.homes.get(agentId) : undefined;
    if (home) this.activeFloor.set(home.building, home.level);
  }

  /** A câmera acompanha este agente (botão Seguir do painel). */
  follows(agentId: string): boolean {
    return this.follow === agentId;
  }

  /** Para de seguir, sem mexer na seleção. */
  stopFollow(): void {
    this.setFollow(null);
  }

  /** Centralizar: a câmera vai ao agente uma vez, sem seguir. */
  center(agentId: string): void {
    const actor = this.actors.get(agentId);
    if (!actor) return;
    this.setFollow(null);
    this.camera.x = actor.x;
    this.camera.y = actor.y - 12;
    const home = this.layout?.homes.get(agentId);
    if (home) this.activeFloor.set(home.building, home.level);
  }

  /** Centraliza a sala de um projeto (Room.id) no campus, mostrando o andar dela. */
  centerRoom(roomId: string): void {
    for (const building of this.layout?.buildings ?? []) {
      for (const floor of building.floors) {
        const room = floor.rooms.find((r) => r.roomId === roomId);
        if (!room) continue;
        this.setFollow(null);
        this.activeFloor.set(building.id, floor.level);
        this.camera.x = (room.x + room.w / 2) * TILE;
        this.camera.y = (room.y + room.h / 2) * TILE;
        return;
      }
    }
  }

  /** Visão geral (barra de ferramentas): enquadra o campus inteiro e para de seguir um agente. */
  fitAll(): void {
    const bounds = this.camera.bounds;
    if (!bounds) return;
    this.setFollow(null);
    this.camera.fitOverview(bounds, this.view());
  }

  /** Aproxima e afasta pelo centro da vista (botões + e − da barra), nos limites de zoom da câmera. */
  zoomIn(): void {
    this.zoomCentro(1);
  }

  zoomOut(): void {
    this.zoomCentro(-1);
  }

  /** Move a câmera em casas (tiles) pelas setas e WASD (shortcuts.ts). Para de seguir um agente, como o arrastar. */
  moveCamera(casasX: number, casasY: number): void {
    this.setFollow(null);
    const zoom = this.camera.zoom;
    this.camera.pan(-casasX * TILE * zoom, -casasY * TILE * zoom);
  }

  private zoomCentro(delta: number): void {
    const view = this.view();
    this.camera.zoomAt(delta, view.w / 2, view.h / 2, view);
  }

  /** Troca quem a câmera segue e avisa a UI só quando muda de fato. */
  private setFollow(agentId: string | null): void {
    if (this.follow === agentId) return;
    this.follow = agentId;
    this.opts.onFollow?.(agentId);
  }

  /**
   * Timelapse (B21): com `agents`, o campus mostra esses status no lugar dos atuais, e quem não
   * está no quadro fica escondido. Com null, volta ao vivo. Os dados ao vivo não mudam.
   */
  playback(agents: ReadonlyArray<Pick<Agent, 'id' | 'status'>> | null): void {
    this.playing = agents ? new Map(agents.map((a) => [a.id, a.status])) : null;
    // timelapse (7.2): enquanto o replay roda, o campus fica em sépia (style.css, #office.replay)
    this.canvas.classList.toggle('replay', this.playing !== null);
  }

  /** Quantos agentes que estão trabalhando aparecem na tela agora (o som de teclado, sound.ts). */
  trabalhandoNaTela(): number {
    if (this.playing) return 0;
    const visible = this.visiveis();
    let n = 0;
    for (const actor of this.actors.values()) {
      if (actor.leaving || !visible.has(actor.floorKey)) continue;
      if (this.statusOf(actor.id) === 'working') n++;
    }
    return n;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.quadro);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  /**
   * Um quadro do laço. No celular (B29-M6), os quadros fora da cadência de 30 por segundo são pulados
   * sem avançar o tempo da animação. Com a aba oculta, não agenda o próximo.
   */
  private readonly quadro = (now: number): void => {
    if (!this.running || this.abaOculta) return;
    const cadencia = cadenciaDoDesenho(now, this.proximoDesenho, this.celular);
    this.proximoDesenho = cadencia.proximo;
    if (!cadencia.desenha) {
      this.raf = requestAnimationFrame(this.quadro);
      return;
    }
    const dt = Math.min(64, now - this.last);
    this.last = now;
    this.update(dt, now);
    this.draw(now);
    this.raf = requestAnimationFrame(this.quadro);
  };

  /**
   * Aba oculta (B29-M6, só no celular): o laço para e, ao voltar, a contagem recomeça de agora, então
   * a animação não dá salto pelo tempo parado.
   */
  private mudouAba(): void {
    if (!this.celular) return;
    const oculta = document.visibilityState === 'hidden';
    if (oculta === this.abaOculta) return;
    this.abaOculta = oculta;
    if (oculta) {
      cancelAnimationFrame(this.raf);
      return;
    }
    if (!this.running) return;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.quadro);
  }

  // ------------------------------------------------------------------ mundo

  /** Remonta o layout quando mudam as salas ou quantas pessoas cada uma tem. */
  private sync(): void {
    const agents = [...this.agentMap.values()];
    const signature = `${this.rooms.map((r) => `${r.id}:${r.agentIds.length}`).join('|')}#${JSON.stringify(this.settings?.campus ?? null)}#${this.settings?.theme.season ?? ''}`;
    if (signature !== this.signature || !this.layout) {
      this.signature = signature;
      this.layout = layoutCampus(this.rooms, agents, this.settings?.campus);
      this.buildWorld(this.layout);
    }
    const alive = new Set(agents.map((a) => a.id));
    for (const id of this.lastActivity.keys()) if (!alive.has(id)) this.lastActivity.delete(id);
    for (const actor of [...this.actors.values()]) {
      if (alive.has(actor.id) || actor.leaving) continue;
      for (const [spot, who] of this.reserved) if (who === actor.id) this.reserved.delete(spot);
      // a sessão saiu da lista: anda até o elevador do andar e some lá (saída.ts)
      this.leave(actor, performance.now());
    }
  }

  /**
   * Saindo: anda até o elevador do andar; sem elevador, até a porta da sala (ou a ponta do corredor).
   * Sem rota para nenhum desses, some na hora.
   */
  private leave(actor: Actor, now: number): void {
    const floor = this.floors.get(actor.floorKey);
    if (!floor) {
      this.actors.delete(actor.id);
      return;
    }
    const from = { x: Math.floor(actor.x / TILE), y: Math.floor((actor.y - 1) / TILE) };
    const elevador = alvoDaSaida(floor.floor);
    const porta = entryOf(floor.floor, this.layout?.homes.get(actor.id)?.room);
    const alvos = [...(elevador ? [{ ...elevador, elevador: true }] : []), { ...porta, elevador: false }];
    let escolhido = { ...porta, elevador: false };
    let path: Array<{ x: number; y: number }> = [];
    for (const alvo of alvos) {
      const rota = findPath(floor.grid, from.x, from.y, alvo.x, alvo.y);
      if (rota.length || (from.x === alvo.x && from.y === alvo.y)) {
        escolhido = alvo;
        path = rota;
        break;
      }
    }
    actor.spot = undefined;
    actor.path = path;
    actor.leaving = { until: path.length ? null : now + SAIDA_FADE_MS, elevador: escolhido.elevador };
  }

  private buildWorld(layout: CampusLayout): void {
    this.floors.clear();
    for (const building of layout.buildings) {
      for (const floor of building.floors) {
        const key = floorKey(building.id, floor.level);
        this.floors.set(key, {
          key,
          building,
          floor,
          backdrop: this.bakeBackdrop(floor),
          rooms: [...floor.rooms, floor.corridor].map((p) => buildRoom(p.map, p.x * TILE, p.y * TILE)),
          grid: floorGrid(floor),
          spots: floorSpots(floor),
        });
      }
      if (!this.activeFloor.has(building.id)) this.activeFloor.set(building.id, 0);
    }
    this.exterior = this.bakeExterior(layout);
    this.tema = temaEfetivo(location.search, this.settings?.theme.season, new Date());
    this.chapeu = chapeuDaEpoca(this.tema);
    this.props = this.exteriorProps(layout);
    this.cars = this.makeCars(layout);
    // vaga-lumes no jardim: acima da calçada, fora dos prédios (com uma folga de 1 tile)
    const predios = layout.buildings.map((b) => ({ x: (b.x - 1) * TILE, y: (b.y - 1) * TILE, w: (b.w + 2) * TILE, h: (b.h + 2) * TILE }));
    this.vagalumes = vagalumesNoJardim(layout.w * TILE, layout.sidewalkY * TILE, predios, 24);
    this.camera.bounds = { x: 0, y: 0, w: layout.w * TILE, h: layout.h * TILE };
    // personagens trocam de andar/sala: recalculam o caminho (quem está saindo segue o seu)
    for (const actor of this.actors.values()) {
      if (actor.leaving) continue;
      actor.spot = undefined;
      actor.path = [];
    }
    this.reserved.clear();
    this.rodas.reset();
    if (!this.fitted && layout.buildings[0]) {
      const sala = toqueFino() ? this.salaInicial(layout) : null;
      if (sala) {
        // celular: começa na sala com mais gente trabalhando, para ver o movimento de cara
        this.activeFloor.set(sala.building, sala.level);
        this.camera.fit(sala.rect, this.view(), 24);
      } else {
        const b = layout.buildings[0];
        this.camera.fit({ x: b.x * TILE, y: b.y * TILE, w: Math.min(b.w, 48) * TILE, h: (b.h + 4) * TILE }, this.view());
        this.camera.zoom = Math.max(2, this.camera.zoom);
      }
      this.fitted = true;
    }
  }

  /**
   * Zoom inicial no celular: a sala de projeto com mais agentes trabalhando (ou pensando); sem nenhum,
   * a com mais gente. Sem sala de projeto, null.
   */
  private salaInicial(layout: CampusLayout): { building: string; level: number; rect: { x: number; y: number; w: number; h: number } } | null {
    let melhor: { score: number; building: string; level: number; rect: { x: number; y: number; w: number; h: number } } | null = null;
    for (const building of layout.buildings) {
      for (const floor of building.floors) {
        for (const placed of floor.rooms) {
          const room = placed.roomId ? this.rooms.find((r) => r.id === placed.roomId) : undefined;
          if (!room?.agentIds.length) continue;
          const trabalhando = room.agentIds.filter((id) => ['working', 'thinking'].includes(this.statusOf(id) ?? '')).length;
          const score = trabalhando * 1000 + room.agentIds.length;
          // a placa da sala fica logo acima do retângulo: entra no enquadramento
          if (!melhor || score > melhor.score) melhor = { score, building: building.id, level: floor.level, rect: { x: placed.x * TILE, y: placed.y * TILE - 28, w: placed.w * TILE, h: placed.h * TILE + 28 } };
        }
      }
    }
    return melhor;
  }

  /** A casca do prédio: paredes grossas entre as salas e as passagens das portas. */
  private bakeBackdrop(floor: CampusFloor): BuiltFloor['backdrop'] {
    const { x, y, w, h } = floor.bounds;
    const canvas = makeCanvas(w * TILE, h * TILE);
    const ctx = ctx2d(canvas);
    ctx.fillStyle = color('black1');
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // telhado: faixa de cima mais clara, com a borda da Agzos
    ctx.fillStyle = color('black2');
    ctx.fillRect(0, 0, canvas.width, TILE);
    ctx.fillStyle = color('red1');
    ctx.fillRect(0, TILE - 2, canvas.width, 2);
    ctx.fillStyle = color('wall0');
    ctx.fillRect(0, canvas.height - 3, canvas.width, 3);
    const tile = TILE_BY_ID.get('porcelanato');
    for (const d of floor.doorways) if (tile) ctx.drawImage(bake(tile.sprite), (d.x - x) * TILE, (d.y - y) * TILE);
    return { canvas, x: x * TILE, y: y * TILE };
  }

  /** Grama, calçada e rua, assadas uma vez em pedaços de CHUNK tiles (canvas tem limite de tamanho). */
  private bakeExterior(layout: CampusLayout): Array<{ canvas: HTMLCanvasElement; x: number; y: number }> {
    const CHUNK = 64;
    const chunks: Array<{ canvas: HTMLCanvasElement; x: number; y: number; ctx: CanvasRenderingContext2D }> = [];
    for (let cx = 0; cx < layout.w; cx += CHUNK) {
      const canvas = makeCanvas(Math.min(CHUNK, layout.w - cx) * TILE, layout.h * TILE);
      chunks.push({ canvas, x: cx * TILE, y: 0, ctx: ctx2d(canvas) });
    }
    const put = (id: string, tx: number, ty: number) => {
      const t = TILE_BY_ID.get(id);
      const chunk = chunks[Math.floor(tx / CHUNK)];
      if (!t || !chunk) return;
      const options = [t.sprite, ...(t.variants ?? [])];
      chunk.ctx.drawImage(bake(options[stableHash(`${tx}:${ty}`) % options.length]!), tx * TILE - chunk.x, ty * TILE);
    };
    for (let ty = 0; ty < layout.h; ty++) {
      for (let tx = 0; tx < layout.w; tx++) {
        let id = 'grama';
        if (ty === layout.sidewalkY || ty === layout.sidewalkY + 1 || ty === layout.streetY + layout.streetH) id = 'calcada';
        if (ty === layout.streetY - 1) id = 'meio-fio';
        if (ty >= layout.streetY && ty < layout.streetY + layout.streetH) id = ty === layout.streetY + 1 ? 'asfalto-faixa' : 'asfalto';
        put(id, tx, ty);
      }
    }
    // praça de pedra na frente de cada prédio
    for (const b of layout.buildings) for (let tx = b.x; tx < b.x + b.w; tx++) put('pedra-praca', tx, b.y + b.h);
    return chunks.map(({ canvas, x, y }) => ({ canvas, x, y }));
  }

  private exteriorProps(layout: CampusLayout): Prop[] {
    const props: Prop[] = [];
    const add = (id: string, tx: number, ty: number) => {
      const def = FURNITURE_BY_ID.get(id);
      if (def) props.push({ def, x: tx * TILE, y: (ty + def.footprint.h) * TILE, flip: stableHash(`${id}${tx}`) % 2 === 0 });
    };
    const trees = ['arvore', 'arvore-pinheiro', 'arvore-pequena', 'arbusto'];
    const occupied = (tx: number, w: number) => layout.buildings.some((b) => tx + w > b.x - 1 && tx < b.x + b.w + 1);
    // fileira de árvores no fundo e no meio dos prédios
    for (let tx = 1; tx < layout.w - 2; tx += 3) {
      const id = trees[stableHash(`fundo${tx}`) % trees.length]!;
      add(id, tx, 0);
      if (!occupied(tx, 2)) add(trees[stableHash(`meio${tx}`) % 3]!, tx, 6 + (stableHash(`m${tx}`) % 8));
    }
    // postes e bancos na calçada
    for (let tx = 2; tx < layout.w - 1; tx += 8) {
      add('poste', tx, layout.sidewalkY - 1);
      if (stableHash(`banco${tx}`) % 3 === 0) add('banco-praca', tx + 3, layout.sidewalkY - 1);
    }
    // árvores do outro lado da rua
    for (let tx = 1; tx < layout.w - 2; tx += 4) add(trees[stableHash(`sul${tx}`) % trees.length]!, tx, layout.streetY + layout.streetH + 1);
    // decoração da época (Halloween, Natal, festa junina), quando houver (?tema=natal força, ver ceu.ts)
    const season = this.tema;
    if (season) {
      for (const d of seasonalDecor(layout, season)) {
        const def = FURNITURE_BY_ID.get(d.item);
        if (!def) continue;
        if (def.wall || d.wall) {
          // na face da parede norte da sala: desenha antes de tudo (sortY bem baixo)
          props.push({ def, x: d.x * TILE, y: d.y * TILE - WALL_H + 3 + def.sprite.h, flip: false, sortY: -1e9 });
        } else {
          add(d.item, d.x, d.y);
        }
      }
    }
    return props;
  }

  private makeCars(layout: CampusLayout): Car[] {
    const ids = ['carro-vermelho', 'carro-azul', 'carro-branco', 'carro-preto'];
    return ids.flatMap((id, i) => {
      const def = FURNITURE_BY_ID.get(id);
      if (!def) return [];
      const lane = i % 2;
      return [{ def, x: ((i * 37) % layout.w) * TILE, y: (layout.streetY + (lane === 0 ? 2 : 4)) * TILE, speed: (lane === 0 ? 1 : -1) * (0.03 + i * 0.006) }];
    });
  }

  // ------------------------------------------------------------------ simulação

  private update(dt: number, now: number): void {
    // toque longo ainda com o dedo parado, e a inércia do arrasto (mesmo sem layout, a câmera anda)
    this.gesto(this.gestos.tick(performance.now()));
    this.passoDaInercia();
    const layout = this.layout;
    if (!layout) return;
    const empty = new Map<string, number>();
    for (const room of this.rooms) if (!room.agentIds.length) empty.set(room.id, this.roomEmpty.get(room.id) ?? now);
    this.roomEmpty = empty;
    // salas de projeto: escurecem e clareiam aos poucos conforme ficam vazias (luz.ts)
    const escuro = new Map<string, number>();
    for (const room of this.rooms) {
      const alvo = isRoomDark(this.roomEmpty.get(room.id), now) ? 1 : 0;
      escuro.set(room.id, aproximaEscuro(this.escuro.get(room.id) ?? 0, alvo, dt));
    }
    this.escuro = escuro;
    this.atualizaRodas(now);
    for (const live of this.agentMap.values()) {
      const agent = this.shown(live);
      if (!agent) continue;
      const home = layout.homes.get(agent.id);
      if (!home) continue;
      const key = floorKey(home.building, home.level);
      const floor = this.floors.get(key);
      if (!floor) continue;
      const actor = this.actorFor(agent, floor, now);
      const target = this.rodas.spotOf(agent.id) ?? chooseSpot(agent, home, floor.spots, this.reserved);
      if (target && target.id !== actor.spot?.id) {
        if (actor.spot) this.reserved.delete(actor.spot.id);
        this.reserved.set(target.id, agent.id);
        actor.spot = target;
        const from = { x: Math.floor(actor.x / TILE), y: Math.floor((actor.y - 1) / TILE) };
        actor.path = findPath(floor.grid, from.x, from.y, target.x, target.y);
        if (!actor.path.length) this.place(actor, target);
      }
      this.walk(actor, dt);
    }
    for (const actor of [...this.actors.values()]) {
      const saindo = actor.leaving;
      if (!saindo) continue;
      if (saindo.until === null) {
        this.walk(actor, dt);
        // chegou ao elevador (ou à porta): some daqui a pouco, e o elevador faz o ding
        if (!actor.path.length) {
          saindo.until = now + SAIDA_FADE_MS;
          if (saindo.elevador) avisaElevador(actor.id);
        }
      } else if (now >= saindo.until) {
        this.actors.delete(actor.id);
      }
    }
    this.chats = idleChats(this.seated(), Date.now());
    for (const car of this.cars) {
      car.x += car.speed * dt;
      const w = layout.w * TILE;
      if (car.x > w + 48) car.x = -48;
      if (car.x < -48) car.x = w + 48;
    }
    if (this.follow) {
      const actor = this.actors.get(this.follow);
      if (actor) {
        this.camera.x += (actor.x - this.camera.x) * 0.08;
        this.camera.y += (actor.y - 12 - this.camera.y) * 0.08;
      }
    }
  }

  /** As rodas: oferece os ociosos de cada andar, refaz quem está em qual roda e anda o relógio delas. */
  private atualizaRodas(now: number): void {
    const layout = this.layout;
    if (!layout) return;
    const rodas = this.rodas;
    rodas.reduzido = reducedMotion();
    rodas.begin();
    for (const live of this.agentMap.values()) {
      const agent = this.shown(live);
      const home = agent ? layout.homes.get(agent.id) : undefined;
      if (agent && home) rodas.offer(agent, floorKey(home.building, home.level));
    }
    rodas.commit(now, this.floors, this.social, () => {
      const out: Array<{ id: string; nome: string }> = [];
      for (const a of this.agentMap.values()) {
        const nome = nomeDoElenco(a.look);
        if (nome) out.push({ id: a.id, nome });
      }
      return out;
    });
    rodas.tick(now, {
      chegou: (id, spot) => {
        const actor = this.actors.get(id);
        return !!actor && !actor.leaving && actor.path.length === 0 && actor.spot?.id === spot.id;
      },
      audivel: (key, x, y) => {
        const b = this.camera.bounds;
        return !this.playing && this.visiveis().has(key) && (!b || (x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h));
      },
      toca: tocaRoda,
      epoch: Date.now(),
    });
  }

  /** Ociosos já sentados (copa ou lounge), para o balão de conversa. */
  private seated(): Seated[] {
    const out: Seated[] = [];
    for (const actor of this.actors.values()) {
      if (actor.leaving || !actor.spot || actor.path.length || actor.agent.status !== 'idle' || !this.agentMap.has(actor.id)) continue;
      out.push({ id: actor.id, spot: actor.spot });
    }
    return out;
  }

  private actorFor(agent: Agent, floor: BuiltFloor, now: number): Actor {
    let actor = this.actors.get(agent.id);
    if (actor && actor.floorKey === floor.key) {
      actor.agent = agent;
      // voltou enquanto saía: cancela a saída e recalcula o caminho daqui
      if (actor.leaving) {
        actor.leaving = null;
        actor.path = [];
      }
      return actor;
    }
    // primeira vez (ou trocou de andar): quem já estava na tela nasce no lugar; quem chega
    // depois entra pela porta da sala do pai (subagente) ou pela ponta do corredor, e anda até a mesa
    const fresh = now - this.loadedAt < 1500;
    const parentRoom = agent.parentId ? this.layout?.homes.get(agent.parentId)?.room : undefined;
    const at = entryOf(floor.floor, fresh ? undefined : parentRoom);
    actor = {
      id: agent.id,
      agent,
      x: at.x * TILE + TILE / 2,
      y: at.y * TILE + 14,
      tx: 0,
      ty: 0,
      path: [],
      dir: 'down',
      spot: undefined,
      floorKey: floor.key,
      leaving: null,
    };
    if (fresh) {
      const home = this.layout?.homes.get(agent.id);
      const spot = home ? chooseSpot(agent, home, floor.spots, this.reserved) : undefined;
      if (spot) {
        this.reserved.set(spot.id, agent.id);
        actor.spot = spot;
        this.place(actor, spot);
      }
    }
    this.actors.set(agent.id, actor);
    return actor;
  }

  private place(actor: Actor, spot: WorldSpot): void {
    actor.x = spot.x * TILE + TILE / 2;
    actor.y = spot.y * TILE + (spot.kind === 'standing' ? 14 : 12);
    actor.dir = spot.facing;
    actor.path = [];
  }

  private walk(actor: Actor, dt: number, speed = SPEED): void {
    const next = actor.path[0];
    if (!next) return;
    const last = actor.path.length === 1;
    const seated = last && actor.spot && actor.spot.kind !== 'standing';
    const gx = next.x * TILE + TILE / 2;
    const gy = next.y * TILE + (seated ? 12 : 14);
    const dx = gx - actor.x;
    const dy = gy - actor.y;
    const dist = Math.hypot(dx, dy);
    const step = speed * dt;
    if (Math.abs(dx) > Math.abs(dy)) actor.dir = dx > 0 ? 'right' : 'left';
    else if (dy !== 0) actor.dir = dy > 0 ? 'down' : 'up';
    if (dist <= step) {
      actor.x = gx;
      actor.y = gy;
      actor.path.shift();
      if (!actor.path.length && actor.spot) actor.dir = actor.spot.facing;
    } else {
      actor.x += (dx / dist) * step;
      actor.y += (dy / dist) * step;
    }
  }

  /** Atividade social atual do personagem (emoji + texto curto), enquanto ainda vale. */
  private socialLine(castId: string): string {
    const act = this.social[castId]?.activity;
    return act && act.until > Date.now() ? `${act.emoji} ${act.text}`.slice(0, 34) : '';
  }

  /** Guarda a atividade mais recente de cada agente (antes mesmo do personagem existir na tela). */
  private lembraAtividade(activity: Activity, live: boolean): void {
    // ao vivo, o balão começa agora; no snapshot, já começa com a idade que a atividade tem
    const idade = live ? 0 : Math.max(0, Date.now() - activity.at);
    this.lastActivity.set(activity.agentId, { activity, at: performance.now() - idade });
    // a tela da mesa segue o tipo da atividade mais recente
    this.lastKind.set(activity.agentId, { kind: activity.kind, at: performance.now() - idade });
  }

  // ------------------------------------------------------------------ telas

  /** Última atividade de cada agente (tipo e quando): a animação da tela da mesa dele segue isso. */
  private lastKind = new Map<string, { kind: string; at: number }>();
  /** Quem sentou por último em cada mesa (tile do assento → id): o ✓ de "concluiu" fica nela um pouco. */
  private lastDesk = new Map<string, string>();

  /** Quem está sentado agora (já chegou ao ponto de trabalho), pelo tile do assento. Lembra a mesa de cada um (lastDesk). */
  private seatedAt(): Map<string, Agent> {
    const out = new Map<string, Agent>();
    for (const actor of this.actors.values()) {
      if (actor.leaving || !actor.spot || actor.spot.use !== 'work' || actor.path.length) continue;
      const agent = this.shown(this.agentMap.get(actor.id) ?? actor.agent);
      if (!agent) continue;
      const key = `${actor.spot.x},${actor.spot.y}`;
      out.set(key, agent);
      this.lastDesk.set(key, agent.id);
    }
    return out;
  }

  /**
   * Quem acabou de sair desta mesa com "concluiu": ocioso, com a atividade `done` ainda recente.
   * Quando o turno acaba o agente já vai para a copa, então o ✓ fica na mesa só enquanto ele anda.
   */
  private leftDone(key: string, now: number): boolean {
    const id = this.lastDesk.get(key);
    const last = id ? this.lastKind.get(id) : undefined;
    if (!id || !last) return false;
    return last.kind === 'done' && this.statusOf(id) === 'idle' && screenMode('idle', last.kind, now - last.at) === 'done';
  }

  /** Telas de uma sala: cada monitor mostra o que quem está sentado ali está fazendo (apagado se ninguém). */
  private screensOf(room: BuiltRoom, seated: Map<string, Agent>, now: number): Drawable[] {
    const tx = room.ox / TILE;
    const ty = room.oy / TILE;
    return room.screens.map((screen) => {
      const key = `${tx + screen.seat.x},${ty + screen.seat.y}`;
      const agent = seated.get(key);
      const last = agent ? this.lastKind.get(agent.id) : undefined;
      const telas = telasEfetivas(this.settings?.theme.screens ?? 'anim', this.bateria, this.celular);
      const vivo = agent ? screenMode(agent.status, last?.kind, last ? now - last.at : Infinity) : this.leftDone(key, now) ? 'done' : 'off';
      const mode = telas === 'off' ? 'off' : vivo;
      const parada = telas === 'still' || reducedMotion();
      return { sortY: screen.sortY, draw: (c, t) => paintScreen(c, screen.box, mode, screenFrame(mode, t, parada)) };
    });
  }

  // ------------------------------------------------------------------ desenho

  /** O que o efeito do GitHub precisa saber do mundo: a sala de cada um, o retângulo dela e onde o personagem está. */
  private githubHost(): FxHost {
    return {
      roomOf: (agentId, project) => (this.rooms.find((r) => r.agentIds.includes(agentId)) ?? this.rooms.find((r) => r.project === project))?.id,
      rectOf: (roomId) => {
        for (const floor of this.visibleFloors()) {
          const placed = floor.floor.rooms.find((r) => r.roomId === roomId);
          if (placed) return { x: placed.x * TILE, y: placed.y * TILE, w: placed.w * TILE, h: placed.h * TILE };
        }
        return null;
      },
      actorAt: (agentId) => {
        const actor = this.actors.get(agentId);
        return actor && this.floors.has(actor.floorKey) && this.visibleFloors().some((f) => f.key === actor.floorKey) ? { x: actor.x, y: actor.y } : null;
      },
    };
  }

  private view() {
    return { w: this.canvas.width, h: this.canvas.height };
  }

  /** devicePixelRatio do campus: no celular, com teto (B29-M6). Todo cálculo de pixel usa este. */
  private dpr(): number {
    return dprDoCampo(window.devicePixelRatio, this.celular);
  }

  /** Como o agente aparece agora: no timelapse, com o status do quadro (null = não está no quadro). */
  private shown(agent: Agent): Agent | null {
    if (!this.playing) return agent;
    const status = this.playing.get(agent.id);
    return status ? { ...agent, status } : null;
  }

  /** Status mostrado de um agente vivo (no timelapse, o do quadro). */
  private statusOf(id: string): Agent['status'] | undefined {
    const live = this.agentMap.get(id);
    return live ? this.shown(live)?.status : undefined;
  }

  private visibleFloors(): BuiltFloor[] {
    const out: BuiltFloor[] = [];
    for (const [building, level] of this.activeFloor) {
      const floor = this.floors.get(floorKey(building, level));
      if (floor) out.push(floor);
    }
    return out;
  }

  private draw(now: number): void {
    const floors = this.visibleFloors();
    const drawables: Drawable[] = [];
    const background: Array<{ canvas: HTMLCanvasElement; x: number; y: number }> = [];
    background.push(...this.exterior);
    const visibleKeys = new Set(floors.map((f) => f.key));
    this.hoverCard(visibleKeys);
    const seated = this.seatedAt();
    for (const floor of floors) {
      background.push(floor.backdrop);
      for (const room of floor.rooms) {
        background.push(room.background);
        drawables.push(...room.drawables, ...this.screensOf(room, seated, now));
      }
    }
    this.rodas.coleta(visibleKeys, drawables);
    for (const prop of this.props) drawables.push(this.propDrawable(prop));
    for (const car of this.cars) drawables.push({ sortY: car.y, draw: (c) => c.drawImage(bake(car.def.sprite, { flip: car.speed < 0 }), Math.round(car.x), car.y - car.def.sprite.h) });
    for (const actor of this.actors.values()) {
      if (!visibleKeys.has(actor.floorKey)) continue;
      const agent = this.shown(this.agentMap.get(actor.id) ?? actor.agent);
      if (agent) drawables.push(this.actorDrawable(agent, actor));
    }

    const hora = this.horaDoCeu();
    const phase = dayPhase(Math.floor(hora), this.settings?.theme.night);
    const dark = this.darkRooms(floors);
    // a luz das salas que estão quase apagadas já não entra no brilho das lâmpadas
    const darkIds = new Set(dark.filter((d) => d.nivel >= 0.5).map((d) => d.room.id));
    const glows = phase === 'night' || phase === 'dusk' ? this.glows(floors, darkIds) : [];
    const after: Array<(c: CanvasRenderingContext2D, now: number) => void> = [];
    // festa e alarme do GitHub: são do ao vivo, somem no timelapse
    const fx = !this.playing && this.gh.active(now) ? this.gh : null;
    const fxHost = fx ? this.githubHost() : null;
    if (fx && fxHost) {
      drawables.push({ sortY: -1e6, draw: (c, n) => fx.drawFloor(c, n, fxHost) });
      after.push((c, n) => fx.drawAir(c, n, fxHost));
    }
    // raios de sol pelas janelas (ceu.ts), depois o véu do céu e as luzes da noite por cima
    if (raioDoSol(hora)) after.push((c) => this.sunLayer(c, floors, hora));
    const tom = corDoCeu(hora);
    if (tom || glows.length) after.push((c) => this.skyLayer(c, tom, glows));
    if (phase === 'night' || phase === 'dusk') after.push((c, n) => this.nightLights(c, n, phase === 'night'));
    if (dark.length) after.push((c) => this.darkLayer(c, dark));
    renderScene(
      this.ctx,
      this.camera,
      this.view(),
      {
        background,
        drawables,
        after,
        overlays: [this.labelsOverlay(floors), this.actorsOverlay(visibleKeys, now), ...(fx && fxHost ? [fx.overlay(fxHost, { alarm: t('gh.alarme') }, () => this.dpr())] : []), this.tabsOverlay()],
      },
      now,
      '#26472a',
    );
  }

  /** Salas de projeto das áreas visíveis que estão escurecendo ou escuras (nível de 0 a 1, luz.ts). */
  private darkRooms(floors: BuiltFloor[]): Array<{ room: PlacedMap; nivel: number }> {
    return floors.flatMap((f) =>
      f.floor.rooms.flatMap((room) => {
        const nivel = room.roomId ? this.escuro.get(room.roomId) ?? 0 : 0;
        return nivel > 0 ? [{ room, nivel }] : [];
      }),
    );
  }

  private darkLayer(c: CanvasRenderingContext2D, dark: Array<{ room: PlacedMap; nivel: number }>): void {
    c.fillStyle = DARK_ROOM;
    for (const { room: r, nivel } of dark) {
      c.globalAlpha = nivel;
      c.fillRect(r.x * TILE, r.y * TILE, r.w * TILE, r.h * TILE);
    }
    c.globalAlpha = 1;
  }

  private propDrawable(prop: Prop): Drawable {
    const def = prop.def;
    const frames = [def.sprite, ...(def.frames ?? [])];
    return {
      sortY: prop.sortY ?? prop.y,
      draw: (c, now) => {
        const s = frames[Math.floor(now / (def.frameMs ?? 800)) % frames.length] ?? def.sprite;
        c.drawImage(bake(s, { flip: prop.flip }), prop.x + Math.floor((def.footprint.w * TILE - s.w) / 2), prop.y - s.h);
      },
    };
  }

  private lookOf(agent: Agent): Look {
    return agent.appearance ?? (CAST_BY_ID.get(agent.look) ?? castFor(agent.id)).look;
  }

  private actorDrawable(agent: Agent, actor: Actor): Drawable {
    return {
      sortY: actor.y,
      draw: (c, now) => {
        const moving = actor.path.length > 0;
        const pose: Pose = moving ? 'walk' : (this.playing ? undefined : this.rodas.poseOf(agent.id)) ?? poseAt(agent, actor.spot);
        const step = moving ? Math.floor(now / 160) : pose === 'type' ? Math.floor(now / 240) : 0;
        const x = Math.round(actor.x);
        const y = Math.round(actor.y);
        // quem está saindo some aos poucos depois de chegar ao elevador (ou à porta)
        if (actor.leaving?.until != null) c.globalAlpha = leaveAlpha(actor.leaving.until - now);
        if (this.selected === agent.id) {
          c.fillStyle = 'rgba(255, 214, 107, 0.55)';
          c.beginPath();
          c.ellipse(x, y, 9, 3.5, 0, 0, Math.PI * 2);
          c.fill();
        }
        c.fillStyle = 'rgba(0,0,0,0.25)';
        c.fillRect(x - 5, y - 1, 10, 2);
        // na festa do GitHub, todos pulam (o pulo é 0 fora dela)
        const pulo = this.playing ? 0 : Math.round(this.gh.hop(agent.id, now));
        c.drawImage(characterFrame(this.lookOf(agent), actor.dir, pose, step), x - CHAR_W / 2, y - CHAR_H + 1 - pulo);
        // gorro de Natal ou chapéu de bruxa, na época (ceu.ts); a cabeça desce igual à do quadro (sentado ou andando)
        if (this.chapeu) {
          const sentado = pose === 'sit' || pose === 'type' || pose === 'sitRaise';
          desenhaChapeu(c, this.chapeu, x, y - CHAR_H + 1 - pulo, sentado ? SIT_DY : moving && step % 2 === 1 ? 1 : 0);
        }
        // esperando o shell: ampulheta sobre a cabeça (art/ampulheta.ts); quem já está saindo não espera mais
        if (agent.status === 'shell' && !actor.leaving) {
          const quadro = AMPULHETA_QUADROS[ampulhetaQuadro(now, reducedMotion())]!;
          c.drawImage(bake(quadro), x - Math.floor(quadro.w / 2), y - CHAR_H - AMPULHETA_ALTURA - pulo);
        }
        c.globalAlpha = 1;
      },
    };
  }

  /** Luzes acesas: as das salas apagadas ficam de fora. */
  private glows(floors: BuiltFloor[], dark: Set<string>): Array<{ x: number; y: number; color: string; radius: number }> {
    const out = floors.flatMap((f) => f.rooms.flatMap((r, i) => (dark.has(f.floor.rooms[i]?.id ?? '') ? [] : r.glows)));
    for (const prop of this.props) {
      const g = prop.def.glow;
      if (g) out.push({ x: prop.x + (prop.def.footprint.w * TILE) / 2 + (g.dx ?? 0), y: prop.y - prop.def.sprite.h + 4 + (g.dy ?? 0), color: g.color, radius: g.radius * 1.6 });
    }
    return out;
  }

  /** Hora que o céu mostra: a do relógio ou ?hora=21:30. Com o modo noite/dia fixo, meia-noite ou meio-dia. */
  private horaDoCeu(): number {
    const modo = this.settings?.theme.night;
    if (modo === 'on') return 0;
    if (modo === 'off') return 12;
    return horaAgora(new Date(), location.search);
  }

  /** Tom do céu (entardecer, noite ou amanhecer) e as luzes acesas por cima. */
  private skyLayer(c: CanvasRenderingContext2D, tint: string | null, glows: Array<{ x: number; y: number; color: string; radius: number }>): void {
    const b = this.camera.bounds;
    if (!b) return;
    if (tint) {
      c.fillStyle = tint;
      c.fillRect(b.x, b.y, b.w, b.h);
    }
    c.globalCompositeOperation = 'lighter';
    for (const g of glows) {
      const grad = c.createRadialGradient(g.x, g.y, 0, g.x, g.y, g.radius);
      grad.addColorStop(0, g.color);
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      c.fillStyle = grad;
      c.fillRect(g.x - g.radius, g.y - g.radius, g.radius * 2, g.radius * 2);
    }
    c.globalCompositeOperation = 'source-over';
  }

  /** Raios de sol pelas janelas: uma camada pré-desenhada por sala (ceu-desenho.ts), que some com a sala apagada. */
  private sunLayer(c: CanvasRenderingContext2D, floors: BuiltFloor[], hora: number): void {
    for (const f of floors) {
      f.rooms.forEach((room, i) => {
        const placed = f.floor.rooms[i];
        const nivel = placed?.roomId ? this.escuro.get(placed.roomId) ?? 0 : 0;
        const camada = nivel < 1 ? camadaDoSol(room, hora) : null;
        if (!camada) return;
        c.globalAlpha = 1 - nivel;
        c.drawImage(camada, room.ox, room.oy);
      });
    }
    c.globalAlpha = 1;
  }

  /**
   * Luzes da noite (e do entardecer, sem os vaga-lumes): faróis acesos nos carros e, de noite
   * de verdade, os vaga-lumes do jardim. Tudo com brilhos pré-desenhados (ceu-desenho.ts).
   */
  private nightLights(c: CanvasRenderingContext2D, now: number, noite: boolean): void {
    c.globalCompositeOperation = 'lighter';
    const halo = brilho(FAROL_COR, 9);
    const lampada = bake(FAROL);
    for (const car of this.cars) {
      const top = car.y - car.def.sprite.h;
      const paraEsquerda = car.speed < 0;
      for (const p of faroisDoCarro(car.x, top, car.def.sprite.w, car.def.sprite.h, paraEsquerda)) {
        c.drawImage(halo, Math.round(p.x - 9), Math.round(p.y - 9));
        c.drawImage(lampada, Math.round(p.x - 1), Math.round(p.y));
      }
    }
    if (noite) {
      const reduzido = reducedMotion();
      const halo8 = brilho(VAGALUME_COR, 8);
      const pixel = bake(VAGALUME);
      for (const v of this.vagalumes) {
        const p = posicaoDoVagalume(v, now, reduzido);
        c.globalAlpha = p.brilho;
        c.drawImage(halo8, Math.round(p.x - 8), Math.round(p.y - 8));
        c.drawImage(pixel, Math.round(p.x - 1.5), Math.round(p.y - 1.5));
      }
      c.globalAlpha = 1;
    }
    c.globalCompositeOperation = 'source-over';
  }

  /** Placas das salas (projeto: nome + quantos agentes + quem precisa de você) e dos prédios. */
  private labelsOverlay(floors: BuiltFloor[]): Overlay {
    return (c, toScreen) => {
      const dpr = this.dpr();
      c.textBaseline = 'middle';
      for (const floor of floors) {
        const sign = toScreen(floor.floor.bounds.x * TILE + 6, floor.floor.bounds.y * TILE + 7);
        c.font = `700 ${11 * dpr}px system-ui, sans-serif`;
        c.textAlign = 'left';
        c.fillStyle = '#f2f3f7';
        const andar = floor.floor.level === 0 ? t('campus.terreo') : t('campus.andar', { n: floor.floor.level });
        c.fillText(`${nomePredio(floor.building.name)} · ${andar}`, sign.x, sign.y);
        for (const placed of floor.floor.rooms) {
          const p = toScreen((placed.x + placed.w / 2) * TILE, placed.y * TILE - 28);
          const room = placed.roomId ? this.rooms.find((r) => r.id === placed.roomId) : undefined;
          const waiting = room ? room.agentIds.some((id) => this.statusOf(id) === 'waiting') : false;
          const nome = room ? placed.label : nomeArea(placed.map.id, placed.label);
          const text = room ? `${nome}  ${room.agentIds.length}` : nome;
          c.font = `${room ? 700 : 600} ${(room ? 11 : 10) * dpr}px system-ui, sans-serif`;
          pill(c, text, p.x, p.y, room ? (waiting ? '#ffd66b' : '#1b1f2a') : 'rgba(27,31,42,0.75)', room && waiting ? '#16181f' : '#e8ebf2', dpr);
        }
      }
    };
  }

  private actorsOverlay(visible: Set<string>, now: number): Overlay {
    return (c, toScreen) => {
      const dpr = this.dpr();
      const PILL = 16 * dpr;
      // o balão tem rabinho: o espaço entre ele e o nome é o rabinho
      const GAP = 4 * dpr;
      // 1) mede o bloco (nome + balão) de cada personagem
      const blocks: Array<LabelBox & { agent: Agent; label: string; nameW: number; bubble: string; bubbleW: number; waiting: boolean; concluido: boolean; fade: number }> = [];
      for (const actor of this.actors.values()) {
        if (!visible.has(actor.floorKey)) continue;
        const live = this.agentMap.get(actor.id);
        const agent = live && this.shown(live);
        if (!agent) continue;
        // pedido de aprovação é do ao vivo: no timelapse, só vale o status do quadro
        const waiting = agent.status === 'waiting' || (!this.playing && this.permissions.some((p) => p.agentId === agent.id));
        const selected = this.selected === agent.id;
        // de longe, só o nome de quem importa agora (selecionado ou esperando)
        if (this.camera.zoom < 3 && !waiting && !selected) continue;
        const label = `${PROVIDER_TAGS[agent.provider] ?? '??'} ${agent.parentId ? `${agent.name} · sub` : agent.name}`;
        c.font = `700 ${10 * dpr}px system-ui, sans-serif`;
        const nameW = c.measureText(label).width + 12 * dpr;
        // a atividade é do ao vivo: some no timelapse; o 'concluiu em …' fica mais tempo na tela
        const last = this.playing ? undefined : this.lastActivity.get(agent.id);
        const duracao = last?.activity.kind === 'done' ? DONE_BUBBLE_MS : BUBBLE_MS;
        const idade = last ? now - last.at : Infinity;
        const atividade = last && idade < duracao ? last.activity : undefined;
        // a vida social e a conversa dos ociosos são do ao vivo também: somem no timelapse
        const social = this.playing ? '' : this.socialLine(agent.look);
        const conversa = this.playing ? undefined : this.chats.get(actor.id);
        // balões desligados no CMS: só "esperando você" continua (é um pedido, não um enfeite)
        const semBaloes = this.settings?.theme.balloons === false;
        // subagente parado é um que terminou: fica azul 'Concluído' até o servidor tirá-lo (tempo do CMS)
        const concluido = !!agent.parentId && agent.status === 'idle';
        const bubble = concluido ? t('balao.concluido') : semBaloes && !waiting ? '' : textoDoBalao({
          aguardando: waiting,
          pensando: !this.playing && agent.status === 'thinking',
          atividade,
          concluiuEm: agent.lastTurnMs,
          fala: (this.playing ? undefined : this.rodas.falaOf(actor.id)) || social || conversa,
        });
        const fade = atividade && !concluido ? Math.min(1, (duracao - idade) / FADE_MS) : 1;
        c.font = `600 ${10 * dpr}px system-ui, sans-serif`;
        const bubbleW = bubble ? c.measureText(bubble).width + 12 * dpr : 0;
        const h = bubble ? PILL * 2 + GAP : PILL;
        // quem espera o shell tem a ampulheta sobre a cabeça: o nome sobe um pouco para não cobri-la
        const head = toScreen(actor.x, actor.y - CHAR_H - (agent.status === 'shell' ? AMPULHETA_ALTURA + 1 : 0));
        // o bloco termina logo acima da cabeça
        const bottom = head.y - 2 * dpr;
        blocks.push({
          id: actor.id,
          x: head.x,
          y: bottom - h / 2,
          w: Math.max(nameW, bubbleW),
          h,
          priority: (waiting ? 100 : 0) + (selected ? 50 : 0) + (bubble ? 5 : 0),
          keep: waiting || selected,
          agent,
          label,
          nameW,
          bubble,
          bubbleW,
          waiting,
          concluido,
          fade,
        });
      }
      // 2) arruma sem sobrepor e desenha
      const byId = new Map(blocks.map((b) => [b.id, b]));
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      for (const placed of arrangeLabels(blocks)) {
        const b = byId.get(placed.id);
        if (!b) continue;
        const bottom = placed.y + placed.h / 2;
        const nameY = bottom - PILL / 2;
        // fio ligando o bloco que subiu à cabeça
        if (placed.dy < 0) {
          c.fillStyle = 'rgba(27,31,42,0.8)';
          c.fillRect(placed.x - dpr / 2, bottom, dpr, -placed.dy);
        }
        c.font = `700 ${10 * dpr}px system-ui, sans-serif`;
        pill(c, b.label, placed.x, nameY, '#1b1f2a', '#e8ebf2', dpr);
        c.fillStyle = PROVIDER_COLORS[b.agent.provider] ?? '#888';
        c.fillRect(placed.x - b.nameW / 2 + 3 * dpr, nameY - 5 * dpr, 3 * dpr, 10 * dpr);
        if (b.bubble) {
          c.font = `600 ${10 * dpr}px system-ui, sans-serif`;
          c.globalAlpha = b.fade;
          balao(c, b.bubble, placed.x, nameY - PILL - GAP, PILL, GAP, dpr, b.waiting ? '#ffd66b' : b.concluido ? color('fabricBlue3') : '#ffffff');
          c.globalAlpha = 1;
        }
      }
    };
  }

  /** Abas de andar em cima de cada prédio com mais de um andar. */
  private tabsOverlay(): Overlay {
    return (c, toScreen) => {
      const dpr = this.dpr();
      this.tabs = [];
      if (!this.layout) return;
      c.font = `700 ${10 * dpr}px system-ui, sans-serif`;
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      for (const b of this.layout.buildings) {
        if (b.floors.length < 2) continue;
        const active = this.activeFloor.get(b.id) ?? 0;
        const right = toScreen((b.x + b.w) * TILE, b.y * TILE);
        b.floors.forEach((floor, i) => {
          const size = 20 * dpr;
          const x = right.x - (b.floors.length - i) * (size + 3 * dpr);
          const y = right.y - size - 4 * dpr;
          const waiting = this.rooms.some((r) => floor.rooms.some((p) => p.roomId === r.id) && r.agentIds.some((id) => this.statusOf(id) === 'waiting'));
          c.fillStyle = floor.level === active ? '#e03131' : waiting ? '#ffd66b' : '#1b1f2a';
          c.beginPath();
          c.roundRect(x, y, size, size, 4 * dpr);
          c.fill();
          c.fillStyle = floor.level === active || !waiting ? '#ffffff' : '#16181f';
          c.fillText(floor.level === 0 ? t('campus.andar_sigla') : String(floor.level), x + size / 2, y + size / 2 + 0.5 * dpr);
          this.tabs.push({ x, y, w: size, h: size, building: b.id, level: floor.level });
        });
      }
    };
  }

  // ------------------------------------------------------------------ entrada

  private bindInput(): void {
    const canvas = this.canvas;
    const dpr = () => this.dpr();
    const local = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: (e.clientX - rect.left) * dpr(), y: (e.clientY - rect.top) * dpr() };
    };
    // o dedo é dos gestos: o canvas não rola a página nem dá zoom do navegador
    canvas.style.touchAction = 'none';
    canvas.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') {
        this.inerciaAtiva = null;
        // o dedo continua no canvas mesmo saindo dele (arrasto e pinça)
        canvas.setPointerCapture(e.pointerId);
        this.gesto(this.gestos.down(ponto(e)));
        return;
      }
      this.drag = { x: e.clientX, y: e.clientY, moved: false };
    });
    canvas.addEventListener('pointermove', (e) => {
      // no toque não há passar o mouse: o dedo só toca ou arrasta
      if (e.pointerType === 'touch') {
        this.hover = null;
        this.gesto(this.gestos.move(ponto(e)));
        return;
      }
      this.hover = { ...local(e), cx: e.clientX, cy: e.clientY };
    });
    canvas.addEventListener('pointerup', (e) => {
      if (e.pointerType === 'touch') this.gesto(this.gestos.up(ponto(e)));
    });
    canvas.addEventListener('pointercancel', (e) => {
      if (e.pointerType === 'touch') this.gesto(this.gestos.cancel());
    });
    // qualquer toque fora do cartão do toque longo fecha o cartão
    window.addEventListener('pointerdown', () => {
      this.toque = null;
    });
    canvas.addEventListener('pointerleave', () => {
      this.hover = null;
    });
    window.addEventListener('pointermove', (e) => {
      if (!this.drag) return;
      const dx = e.clientX - this.drag.x;
      const dy = e.clientY - this.drag.y;
      if (!this.drag.moved && Math.hypot(dx, dy) < 4) return;
      this.drag.moved = true;
      this.setFollow(null);
      this.camera.pan(dx * dpr(), dy * dpr());
      this.drag.x = e.clientX;
      this.drag.y = e.clientY;
    });
    window.addEventListener('pointerup', (e) => {
      const drag = this.drag;
      this.drag = null;
      if (drag && !drag.moved && e.target === canvas) this.click(local(e));
    });
    canvas.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        const p = local(e);
        this.camera.zoomAt(e.deltaY < 0 ? 1 : -1, p.x, p.y, this.view());
      },
      { passive: false },
    );
  }

  /** Clique do mouse ou toque simples (`toque`: com a área de toque maior): seleciona o personagem ou desmarca. */
  private click(p: { x: number; y: number }, toque = false): void {
    const tab = this.tabAt(p);
    if (tab) {
      this.activeFloor.set(tab.building, tab.level);
      return;
    }
    const hit = this.actorAt(p, this.visiveis(), toque ? this.tolerancia() : 0)?.id ?? null;
    this.selected = hit;
    this.setFollow(hit);
    this.opts.onSelect?.(hit);
  }

  /** Toque duplo: num personagem, segue ele; no chão, aproxima um nível de zoom naquele ponto. */
  private duplo(p: { x: number; y: number }): void {
    if (this.tabAt(p)) return;
    if (this.actorAt(p, this.visiveis(), this.tolerancia())) {
      this.click(p, true);
      return;
    }
    this.setFollow(null);
    this.camera.zoomAt(1, p.x, p.y, this.view());
  }

  /** Toque longo num personagem: abre o cartão de detalhes perto do dedo (se o CMS deixa mostrar cartões). */
  private cartaoDoToque(x: number, y: number): void {
    if (this.settings?.theme.hoverCard === false) return;
    const actor = this.actorAt(this.noCanvas(x, y), this.visiveis(), this.tolerancia());
    this.toque = actor ? { id: actor.id, x, y } : null;
  }

  /** Aplica os gestos de toque reconhecidos (gestures.ts) à câmera, à seleção e ao cartão. */
  private gesto(lista: Gesto[]): void {
    const d = this.dpr();
    for (const g of lista) {
      switch (g.tipo) {
        case 'arrasto':
          this.setFollow(null);
          this.toque = null;
          this.camera.pan(g.dx * d, g.dy * d);
          break;
        case 'pinca': {
          this.setFollow(null);
          this.toque = null;
          // zoom em torno do ponto médio de antes, e só depois ele anda: o que estava sob os dedos fica
          const antes = this.noCanvas(g.x - g.dx, g.y - g.dy);
          this.camera.zoomLivre(this.camera.zoom * g.escala, antes.x, antes.y, this.view());
          this.camera.pan(g.dx * d, g.dy * d);
          break;
        }
        case 'fim-pinca': {
          const p = this.noCanvas(g.x, g.y);
          this.camera.fechaZoom(p.x, p.y, this.view());
          break;
        }
        case 'solta':
          this.inerciaAtiva = { vx: g.vx, vy: g.vy, t0: performance.now(), feito: { dx: 0, dy: 0 } };
          break;
        case 'toque':
          this.click(this.noCanvas(g.x, g.y), true);
          break;
        case 'duplo':
          this.duplo(this.noCanvas(g.x, g.y));
          break;
        case 'longo':
          this.cartaoDoToque(g.x, g.y);
          break;
      }
    }
  }

  /** Inércia do arrasto: a câmera anda o que falta do deslocamento dessa velocidade, até parar em INERCIA_MS. */
  private passoDaInercia(): void {
    const ia = this.inerciaAtiva;
    if (!ia) return;
    const ms = performance.now() - ia.t0;
    const f = inercia(ia.vx, ia.vy, ms);
    const d = this.dpr();
    this.camera.pan((f.dx - ia.feito.dx) * d, (f.dy - ia.feito.dy) * d);
    ia.feito = f;
    if (ms >= INERCIA_MS) this.inerciaAtiva = null;
  }

  /** Ponto da janela (px CSS) no canvas (px, já com devicePixelRatio). */
  private noCanvas(x: number, y: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const d = this.dpr();
    return { x: (x - rect.left) * d, y: (y - rect.top) * d };
  }

  private tabAt(p: { x: number; y: number }) {
    return this.tabs.find((t) => p.x >= t.x && p.x <= t.x + t.w && p.y >= t.y && p.y <= t.y + t.h);
  }

  private visiveis(): Set<string> {
    return new Set(this.visibleFloors().map((f) => f.key));
  }

  /** Área extra em volta dos personagens no toque: TOQUE_TOLERANCIA px CSS, convertidos para px de arte. */
  private tolerancia(): number {
    return (TOQUE_TOLERANCIA * (this.dpr())) / this.camera.zoom;
  }

  /**
   * O personagem sob um ponto do canvas (px), entre os dos andares visíveis; quem está mais à frente ganha.
   * `tol` alarga a área de cada personagem (px de arte) para o dedo acertar.
   */
  private actorAt(p: { x: number; y: number }, visible: Set<string>, tol = 0): Actor | null {
    const world = this.camera.toWorld(p.x, p.y, this.view());
    let hit: Actor | null = null;
    for (const actor of this.actors.values()) {
      if (!visible.has(actor.floorKey) || actor.leaving) continue;
      const dentro = world.x >= actor.x - 7 - tol && world.x <= actor.x + 7 + tol && world.y >= actor.y - CHAR_H - tol && world.y <= actor.y + 2 + tol;
      if (dentro && (!hit || actor.y >= hit.y)) hit = actor;
    }
    return hit;
  }

  /**
   * Cartão ao passar o mouse sobre um personagem, ao lado do cursor. Some ao arrastar a câmera e
   * quando o mouse sai do canvas; sobre um personagem o cursor vira mão.
   */
  private hoverCard(visible: Set<string>): void {
    if (this.toque) {
      this.cartaoDoToqueNaTela(visible);
      return;
    }
    const hover = this.drag?.moved ? null : this.hover;
    const actor = hover ? this.actorAt(hover, visible) : null;
    const agent = actor ? this.shown(this.agentMap.get(actor.id) ?? actor.agent) : null;
    const cursor = agent ? 'pointer' : '';
    if (this.canvas.style.cursor !== cursor) this.canvas.style.cursor = cursor;
    if (!hover || !agent || this.settings?.theme.hoverCard === false) {
      this.hideCard();
      return;
    }
    const html = this.cartaoHtml(agent);
    const key = `${hover.cx},${hover.cy},${html}`;
    if (key === this.cardKey) return;
    this.cardKey = key;
    const el = this.cardEl();
    el.innerHTML = html;
    el.hidden = false;
    // do lado do cursor que couber na janela
    const margem = 16;
    const x = hover.cx + margem + el.offsetWidth > window.innerWidth ? hover.cx - margem - el.offsetWidth : hover.cx + margem;
    const y = hover.cy + margem + el.offsetHeight > window.innerHeight ? hover.cy - margem - el.offsetHeight : hover.cy + margem;
    el.style.left = `${Math.max(4, x)}px`;
    el.style.top = `${Math.max(4, y)}px`;
  }

  /** Cartão do toque longo: centrado acima do dedo (embaixo, se não couber); some quando o personagem some. */
  private cartaoDoToqueNaTela(visible: Set<string>): void {
    const alvo = this.toque;
    const actor = alvo ? this.actors.get(alvo.id) : undefined;
    const agent = actor && visible.has(actor.floorKey) ? this.shown(this.agentMap.get(actor.id) ?? actor.agent) : null;
    if (!alvo || !agent) {
      this.toque = null;
      this.hideCard();
      return;
    }
    const html = this.cartaoHtml(agent);
    const key = `${alvo.x},${alvo.y},${html}`;
    if (key === this.cardKey) return;
    this.cardKey = key;
    const el = this.cardEl();
    el.innerHTML = html;
    el.hidden = false;
    const folga = 28;
    const x = alvo.x - el.offsetWidth / 2;
    const acima = alvo.y - el.offsetHeight - folga;
    const y = acima >= 4 ? acima : alvo.y + folga;
    el.style.left = `${Math.max(4, Math.min(window.innerWidth - el.offsetWidth - 4, x))}px`;
    el.style.top = `${Math.max(4, Math.min(window.innerHeight - el.offsetHeight - 4, y))}px`;
  }

  private cardEl(): HTMLDivElement {
    if (!this.card) {
      this.card = document.createElement('div');
      this.card.className = 'cartao-hover';
      // o cartão repete o que a lateral já mostra: para leitor de tela, fica de fora
      this.card.setAttribute('aria-hidden', 'true');
      document.body.append(this.card);
    }
    return this.card;
  }

  private hideCard(): void {
    if (this.card && !this.card.hidden) this.card.hidden = true;
    this.cardKey = '';
  }

  /** Miolo do cartão: nome e selo da CLI, projeto, status com 'há X' e a última atividade. */
  private cartaoHtml(agent: Agent): string {
    const cli = this.settings?.clis.find((c) => c.id === agent.accountId);
    const letra = cli?.tag || PROVIDER_TAGS[agent.provider] || '?';
    const cor = cli?.color ?? PROVIDER_COLORS[agent.provider] ?? '#888';
    // no timelapse, a atividade e o 'há X' são do ao vivo: ficam de fora
    const aoVivo = !this.playing;
    const ultima = aoVivo ? this.lastActivity.get(agent.id)?.activity : undefined;
    const atividade = ultima ?? (aoVivo && agent.activity ? { kind: '', text: agent.activity } : undefined);
    const c = conteudoDoCartao({
      nome: agent.name,
      subagente: !!agent.parentId,
      projeto: agent.title || agent.project,
      status: agent.status,
      statusDesde: aoVivo ? agent.statusSince : undefined,
      atividade,
      concluiuEm: agent.lastTurnMs,
    });
    return (
      `<div class="ch-nome"><span class="tag" style="background:${escapa(cor)}">${escapa(letra)}</span><b>${escapa(c.nome)}</b></div>` +
      (c.projeto ? `<div class="ch-projeto">${escapa(c.projeto)}</div>` : '') +
      `<div class="ch-status status-${agent.status}"><span class="ponto"></span>${escapa(c.status)}${c.ha ? ` <small>${escapa(c.ha)}</small>` : ''}</div>` +
      (c.atual ? `<div class="ch-atual">${escapa(c.atual)}</div>` : '')
    );
  }
}

/** Nome do prédio como o layout monta ('Sede X', 'Prédio X', 'Coworking'), no idioma atual. */
function nomePredio(name: string): string {
  if (name === 'Coworking') return t('campus.coworking');
  const m = /^(Sede|Prédio) (.+)$/.exec(name);
  if (!m) return name;
  return m[1] === 'Sede' ? t('campus.sede', { nome: m[2]! }) : t('campus.predio', { nome: m[2]! });
}

/** Área comum pelo id do mapa (recepcao, copa…); sala de projeto usa o nome do projeto e não passa aqui. */
function nomeArea(mapId: string, fallback: string): string {
  switch (mapId) {
    case 'recepcao':
      return t('area.recepcao');
    case 'copa':
      return t('area.copa');
    case 'lounge':
      return t('area.lounge');
    case 'banheiros':
      return t('area.banheiros');
    case 'reuniao':
      return t('area.reuniao');
    case 'servidores':
      return t('area.servidores');
    case 'terraco':
      return t('area.terraco');
    default:
      return fallback;
  }
}

/**
 * Balão de fala centrado em (x, y): caixa de borda escura com um rabinho de `cauda` px apontando
 * para baixo, para o nome. Desenha o contorno escuro primeiro e o miolo por cima.
 */
function balao(c: CanvasRenderingContext2D, text: string, x: number, y: number, h: number, cauda: number, dpr: number, fundo: string): void {
  const w = c.measureText(text).width + 12 * dpr;
  const r = 5 * dpr;
  const borda = dpr;
  const base = y + h / 2;
  c.fillStyle = '#16181f';
  c.beginPath();
  c.roundRect(x - w / 2 - borda, y - h / 2 - borda, w + 2 * borda, h + 2 * borda, r + borda);
  c.moveTo(x - 4 * dpr, base - 2 * borda);
  c.lineTo(x + 4 * dpr, base - 2 * borda);
  c.lineTo(x, base + cauda);
  c.fill();
  c.fillStyle = fundo;
  c.beginPath();
  c.roundRect(x - w / 2, y - h / 2, w, h, r);
  c.moveTo(x - 3 * dpr, base - borda);
  c.lineTo(x + 3 * dpr, base - borda);
  c.lineTo(x, base + cauda - 2 * borda);
  c.fill();
  c.fillStyle = '#16181f';
  c.textAlign = 'center';
  c.fillText(text, x, y + 0.5 * dpr);
}

/** Escapa o texto para pôr no HTML do cartão (nome, projeto e cor vêm de fora). */
function escapa(valor: string): string {
  return valor.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch] ?? ch);
}

/** Etiqueta arredondada com texto centralizado. Devolve a largura. */
function pill(c: CanvasRenderingContext2D, text: string, x: number, y: number, bg: string, fg: string, dpr: number): number {
  const w = c.measureText(text).width + 12 * dpr;
  const h = 16 * dpr;
  c.fillStyle = bg;
  c.beginPath();
  c.roundRect(x - w / 2, y - h / 2, w, h, 5 * dpr);
  c.fill();
  c.fillStyle = fg;
  c.textAlign = 'center';
  c.fillText(text, x, y + 0.5 * dpr);
  return w;
}

// ------------------------------------------------------------------ telas

let reduceQuery: MediaQueryList | null | undefined;

/** Movimento reduzido (prefers-reduced-motion): as telas dos monitores ficam num quadro só. */
function reducedMotion(): boolean {
  reduceQuery ??= typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
  return reduceQuery?.matches ?? false;
}

/** Tela de toque sem mouse (celular, tablet): o zoom inicial enquadra a sala com mais movimento. */
function toqueFino(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
}

/** Um ponteiro do DOM para o formato dos gestos (px CSS da janela, relógio de performance.now). */
function ponto(e: PointerEvent): Ponto {
  return { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now() };
}
